import { Eye, ClipboardList, Stethoscope, Sparkles } from "lucide-react";
import { OPTION_MAP, OpticalCombo, VA_OPTIONS } from "./opticalOptions";

// Field helper — renders a dropdown for known optical columns (sph/cyl/axis/add),
// a creatable combo for vision (vn), otherwise a plain text input.
function Field({ label, name, value, onChange, placeholder, col }) {
  const options = col ? OPTION_MAP[col] : null;
  const isVision = col === "vn" || col === "va";
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </label>
      {isVision ? (
        <OpticalCombo
          name={name}
          value={value}
          onChange={onChange}
          options={VA_OPTIONS}
          placeholder={label}
          testId={`eyetest-${name}`}
        />
      ) : options ? (
        <OpticalCombo
          name={name}
          value={value}
          onChange={onChange}
          options={options}
          placeholder={label}
          testId={`eyetest-${name}`}
        />
      ) : (
        <input
          type="text"
          name={name}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder || label}
          data-testid={`eyetest-${name}`}
          className="w-full h-10 mt-1 rounded-md border border-slate-300 px-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
      )}
    </div>
  );
}

/**
 * Row of clinical eye-test values. Renders R and L rows using `columns`.
 * columns = ["sph", "cyl", "axis"] or ["sph", "cyl", "axis", "vn", "add"]
 * prefix = "pg" | "ar1" | "ar2" | "subj" | "dry" | "pmt"
 */
function TestRow({ title, subtitle, tint, icon: Icon, prefix, columns, data, onChange }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`h-9 w-9 rounded-lg grid place-items-center ${tint}`}>
          <Icon size={16} />
        </div>
        <div>
          <h4 className="text-sm font-bold text-slate-900 leading-tight">{title}</h4>
          {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
        </div>
      </div>

      {["r", "l"].map((eye) => (
        <div
          key={eye}
          className="mb-2 last:mb-0 rounded-lg border border-slate-100 bg-slate-50/60 p-3"
        >
          <div className="mb-2 flex items-center gap-1.5">
            <span
              className={`grid h-6 w-6 place-items-center rounded-md text-[10px] font-bold ${
                eye === "r" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"
              }`}
            >
              {eye === "r" ? "R" : "L"}
            </span>
            <span className="text-xs font-medium text-slate-600">
              {eye === "r" ? "Right Eye" : "Left Eye"}
            </span>
          </div>
          <div className={`grid gap-2 grid-cols-${columns.length}`}
               style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
            {columns.map((col) => (
              <Field
                key={col}
                label={col.toUpperCase()}
                name={`${prefix}_${eye}_${col}`}
                value={data[`${prefix}_${eye}_${col}`]}
                onChange={onChange}
                placeholder={col.toUpperCase()}
                col={col}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Vision block — R / L Snellen dropdowns (used for Aided & Unaided vision)
function VisionRow({ title, prefix, data, onChange, tint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`h-9 w-9 rounded-lg grid place-items-center ${tint}`}>
          <Eye size={16} />
        </div>
        <h4 className="text-sm font-bold text-slate-900 leading-tight">{title}</h4>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {["r", "l"].map((eye) => (
          <div key={eye}>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {eye === "r" ? "Right (R)" : "Left (L)"}
            </label>
            <OpticalCombo
              name={`${prefix}_${eye}`}
              value={data[`${prefix}_${eye}`]}
              onChange={onChange}
              options={VA_OPTIONS}
              placeholder="Vision"
              testId={`eyetest-${prefix}_${eye}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function EyeTestFields({ data, onChange }) {
  return (
    <div className="rounded-2xl border border-blue-100 bg-blue-50/30 p-5 space-y-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-sky-500 grid place-items-center shadow-sm">
          <Eye size={18} className="text-white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-900">Eye Testing Chart</h3>
          <p className="text-xs text-slate-500">Clinical exam details for this visit</p>
        </div>
      </div>

      {/* Chief Complaint + History */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <ClipboardList size={13} className="text-slate-500" />
            Chief Complaint
          </label>
          <textarea
            rows={2}
            name="chief_complaint"
            value={data.chief_complaint || ""}
            onChange={onChange}
            placeholder="Description (2 lines)"
            className="mt-1.5 w-full resize-none rounded-md border border-slate-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <ClipboardList size={13} className="text-slate-500" />
            History
          </label>
          <textarea
            rows={2}
            name="history"
            value={data.history || ""}
            onChange={onChange}
            placeholder="Patient history (2 lines)"
            className="mt-1.5 w-full resize-none rounded-md border border-slate-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      {/* Vision (Aided) + Vision (Unaided) — right after History */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VisionRow
          title="Vision (Aided)"
          prefix="vision_aided"
          data={data}
          onChange={onChange}
          tint="bg-emerald-100 text-emerald-700"
        />
        <VisionRow
          title="Vision (Unaided)"
          prefix="vision_unaided"
          data={data}
          onChange={onChange}
          tint="bg-sky-100 text-sky-700"
        />
      </div>

      {/* P.G. + P.G. Remarks */}
      <TestRow
        title="P.G. (Previous Glass)"
        subtitle="Existing spectacle power"
        tint="bg-amber-100 text-amber-700"
        icon={Eye}
        prefix="pg"
        columns={["sph", "cyl", "axis", "add"]}
        data={data}
        onChange={onChange}
      />
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="text-xs font-semibold text-slate-700">P.G. Remarks (1 line)</label>
        <input
          type="text"
          name="pg_remarks"
          value={data.pg_remarks || ""}
          onChange={onChange}
          placeholder="Remarks"
          className="mt-1.5 w-full h-10 rounded-md border border-slate-300 px-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
      </div>

      {/* AR1 + AR2 side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TestRow
          title="AR 1"
          subtitle="Auto Refractor — reading 1"
          tint="bg-sky-100 text-sky-700"
          icon={Stethoscope}
          prefix="ar1"
          columns={["sph", "cyl", "axis"]}
          data={data}
          onChange={onChange}
        />
        <TestRow
          title="AR 2"
          subtitle="Auto Refractor — reading 2"
          tint="bg-sky-100 text-sky-700"
          icon={Stethoscope}
          prefix="ar2"
          columns={["sph", "cyl", "axis"]}
          data={data}
          onChange={onChange}
        />
      </div>

      {/* Subjective Refraction — now full columns, same as Dilated Refraction */}
      <TestRow
        title="Subjective Refraction"
        subtitle="Patient-verified refinement"
        tint="bg-emerald-100 text-emerald-700"
        icon={Sparkles}
        prefix="subj"
        columns={["sph", "cyl", "axis", "vn", "add"]}
        data={data}
        onChange={onChange}
      />

      {/* Subjective Refraction remarks */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="text-xs font-semibold text-slate-700">Subjective Refraction Remarks</label>
        <textarea
          rows={2}
          name="subj_remarks"
          value={data.subj_remarks || ""}
          onChange={onChange}
          placeholder="Remarks for subjective refraction"
          data-testid="eyetest-subj_remarks"
          className="mt-1.5 w-full resize-none rounded-md border border-slate-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
      </div>

      {/* Dilated Refraction + PMT side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TestRow
          title="Dilated Refraction"
          subtitle="Dilated refraction (retinoscopy)"
          tint="bg-rose-100 text-rose-700"
          icon={Eye}
          prefix="dry"
          columns={["sph", "cyl", "axis", "vn", "add"]}
          data={data}
          onChange={onChange}
        />
        <TestRow
          title="PMT"
          subtitle="Post-mydriatic test"
          tint="bg-violet-100 text-violet-700"
          icon={Eye}
          prefix="pmt"
          columns={["sph", "cyl", "axis", "vn", "add"]}
          data={data}
          onChange={onChange}
        />
      </div>

      {/* Final remarks */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="text-xs font-semibold text-slate-700">Final Remarks (2 lines)</label>
        <textarea
          rows={2}
          name="eye_test_remarks"
          value={data.eye_test_remarks || ""}
          onChange={onChange}
          placeholder="Overall observation, recommendation, etc."
          className="mt-1.5 w-full resize-none rounded-md border border-slate-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
      </div>
    </div>
  );
}
